// The one place the application shows a message to the user in a Message Box.
//
// Messages come from two directions:
//  * the SERVER, beside a successful answer - apps/core/user_messages.py puts them
//    in the X-Biztimize-Messages response header and services/api.js hands them
//    here (e.g. "the credit approval reached nobody", SD-ORD-E0001);
//  * a SCREEN, for an error it caught - showApiError(err) turns an axios error
//    into a message, keeping its stable error code.
//
// Each message is { severity: 'error'|'warning'|'info'|'success', text, title?,
// error_code? }. A message with an error code gets a link to the Biztimize Note
// that explains it (components/common/MessageBoxHost.js).
//
// No React here: api.js imports it, and it must not import api.js back.

const listeners = new Set();
const SEVERITIES = ['error', 'warning', 'info', 'success'];

const normalise = (m) => {
  if (!m || typeof m !== 'object' || !m.text) return null;
  return {
    severity: SEVERITIES.includes(m.severity) ? m.severity : 'info',
    text: String(m.text),
    title: m.title ? String(m.title) : '',
    error_code: m.error_code ? String(m.error_code) : '',
  };
};

/** Show one or more messages. Returns how many were shown. */
export function showMessages(messages) {
  const list = (Array.isArray(messages) ? messages : [messages]).map(normalise).filter(Boolean);
  if (list.length) listeners.forEach((fn) => fn(list));
  return list.length;
}

export const showMessage = (severity, text, { title, errorCode } = {}) =>
  showMessages({ severity, text, title, error_code: errorCode });

/** An axios error as a Message Box error, keeping its error code. */
export function showApiError(error, fallbackText = 'The request failed.') {
  const data = error?.response?.data;
  let text = fallbackText;
  if (typeof data === 'string' && data.trim() && data.length < 500) text = data;
  else if (data && typeof data === 'object') {
    text = data.detail || data.error || data.message || fallbackText;
  } else if (error?.message) text = error.message;
  return showMessages({
    severity: 'error',
    text,
    error_code: error?.errorCode || data?.error_code || '',
  });
}

/** Parse the X-Biztimize-Messages header (JSON array). Bad input is ignored. */
export function messagesFromHeader(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn('Unreadable X-Biztimize-Messages header', e);
    return [];
  }
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const messageBox = { showMessages, showMessage, showApiError, subscribe };
export default messageBox;
